import { AddIcon, DeleteIcon } from "@chakra-ui/icons"
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  Radio,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Fragment, useEffect, useMemo, useState } from "react"
import type { ProductListItemDTO } from "../../../api/productList"
import { type TechnologyCreateDTO, TechnologyService } from "../../../client"
import { getLicenseErrorDetails } from "../../../utils/licenseErrors"
import UnifiedProductPickerModal from "../Modals/UnifiedProductPickerModal"
import { RowProductSelector } from "../shared/SingleWarehouseFormSections"
import {
  findTechnologyInputProductConflicts,
  findTechnologyInputProductUsagesOutsideTarget,
  formatTechnologyInputProductUsage,
} from "./technologyInputProductConflicts"
import {
  type TechnologyInputSubstituteDraft,
  updateSubstituteLossPercent,
} from "./technologyInputSubstituteState"

export type { TechnologyInputSubstituteDraft } from "./technologyInputSubstituteState"

export type TechnologyInputDraft = {
  product: ProductListItemDTO | undefined
  qty: string
  loss_percent: string
  service_cost_netto: string
  cost_note: string
  substitutes?: TechnologyInputSubstituteDraft[]
}

type TechnologyOutputDraft = {
  product: ProductListItemDTO | undefined
  qty: string
  is_main: boolean
}

export type TechnologyFormInitialValues = {
  code: string
  name: string
  description: string
  version: string
  isActive: boolean
  requiresExpirationDate: boolean
  validityDays: string
  inputs: TechnologyInputDraft[]
  outputs: TechnologyOutputDraft[]
}

const emptySubstituteRow = (): TechnologyInputSubstituteDraft => ({
  product: undefined,
  qty: "",
  loss_percent: "",
  service_cost_netto: "",
  priority: "1",
  note: "",
})

const emptyInputRow = (): TechnologyInputDraft => ({
  product: undefined as ProductListItemDTO | undefined,
  qty: "",
  loss_percent: "",
  service_cost_netto: "",
  cost_note: "",
  substitutes: [],
})

const emptyOutputRow = (isMain = false) => ({
  product: undefined as ProductListItemDTO | undefined,
  qty: "",
  is_main: isMain,
})

/** Normalizes technology codes to the required uppercase display format. */
const normalizeTechnologyCode = (value: string) =>
  value.trim().toLocaleUpperCase("pl-PL")

type Props = {
  onSuccess?: () => void
  onCreated?: (technology: any) => void
  onCancel?: () => void
  initialValues?: TechnologyFormInitialValues | null
  isVersionLocked?: boolean
  formMode?: "create" | "variant" | "version"
}

type PickerTarget =
  | { type: "input" | "output"; mode: "append" | "replace"; index?: number }
  | {
      type: "substitute"
      mode: "append" | "replace"
      inputIndex: number
      subIndex?: number
    }

type TechnologyCodeConflict = {
  requested: string
  suggested: string | null
}

export default function AddTechnology({
  onSuccess,
  onCreated,
  onCancel,
  initialValues,
  isVersionLocked = true,
  formMode = "create",
}: Props) {
  const toast = useToast()
  const qc = useQueryClient()
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")

  const [code, setCode] = useState("")
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [version, setVersion] = useState("1")
  const [codeConflict, setCodeConflict] =
    useState<TechnologyCodeConflict | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [requiresExpirationDate, setRequiresExpirationDate] = useState(false)
  const [validityDays, setValidityDays] = useState("")

  const [inputs, setInputs] = useState<TechnologyInputDraft[]>([
    emptyInputRow(),
  ])
  const [outputs, setOutputs] = useState([emptyOutputRow(true)])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null)

  const inputProductRows = useMemo(
    () =>
      inputs.map((row) => ({
        productId: row.product?.id,
        productLabel: row.product
          ? `${row.product.sku} — ${row.product.name}`
          : undefined,
        substitutes: (row.substitutes ?? []).map((substitute) => ({
          productId: substitute.product?.id,
          productLabel: substitute.product
            ? `${substitute.product.sku} — ${substitute.product.name}`
            : undefined,
        })),
      })),
    [inputs],
  )
  const inputProductConflicts = useMemo(
    () => findTechnologyInputProductConflicts(inputProductRows),
    [inputProductRows],
  )

  useEffect(() => {
    if (!initialValues) return

    setCode(initialValues.code)
    setName(initialValues.name)
    setDescription(initialValues.description)
    setVersion(initialValues.version)
    setIsActive(initialValues.isActive)
    setRequiresExpirationDate(initialValues.requiresExpirationDate)
    setValidityDays(initialValues.validityDays)
    setInputs(
      initialValues.inputs.length > 0
        ? initialValues.inputs
        : [emptyInputRow()],
    )
    setOutputs(
      initialValues.outputs.length > 0
        ? initialValues.outputs
        : [emptyOutputRow(true)],
    )
  }, [initialValues])

  const isCodeErr = code.trim() === ""
  const isNameErr = name.trim() === ""

  const inputsValid = useMemo(() => {
    return (
      inputs.length > 0 &&
      inputs.every((row) => {
        const qty = Number.parseFloat(row.qty)
        if (!(row.product?.id && Number.isFinite(qty) && qty > 0)) return false
        if (row.product?.is_service) {
          const serviceCost = Number.parseFloat(row.service_cost_netto)
          if (!(Number.isFinite(serviceCost) && serviceCost > 0)) return false
        }
        return (row.substitutes ?? []).every((sub) => {
          const substituteQty = Number.parseFloat(sub.qty)
          if (
            !(
              sub.product?.id &&
              Number.isFinite(substituteQty) &&
              substituteQty > 0
            )
          ) {
            return false
          }
          if (sub.product.is_service) {
            const serviceCost = Number.parseFloat(sub.service_cost_netto)
            return Number.isFinite(serviceCost) && serviceCost > 0
          }
          return true
        })
      })
    )
  }, [inputs])

  const outputsValid = useMemo(() => {
    return (
      outputs.length > 0 &&
      outputs.filter((row) => row.is_main).length === 1 &&
      outputs.every((row) => {
        const qty = Number.parseFloat(row.qty)
        return row.product?.id && Number.isFinite(qty) && qty > 0
      })
    )
  }, [outputs])

  const mainOutputProduct = outputs.find((row) => row.is_main)?.product
  const mainProductRequiresExpiration =
    mainOutputProduct?.requires_expiration_date === true

  const isExpirationValid =
    !mainProductRequiresExpiration || requiresExpirationDate

  const canSubmit =
    !isCodeErr &&
    !isNameErr &&
    inputsValid &&
    inputProductConflicts.length === 0 &&
    outputsValid &&
    isExpirationValid
  const highlightedProductIds = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...inputs,
            ...inputs.flatMap((row) => row.substitutes ?? []),
            ...outputs,
          ]
            .map((row) => row.product?.id)
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [inputs, outputs],
  )

  const { mutate, isPending } = useMutation({
    mutationKey: ["technology", "create"],
    mutationFn: (payload: TechnologyCreateDTO) =>
      TechnologyService.createTechnology(payload),
    onSuccess: async (created) => {
      onCreated?.(created)
      toast({ title: "Technologia została utworzona", status: "success" })
      await qc.invalidateQueries({ queryKey: ["technologies", "list"] })
      setCodeConflict(null)
      setCode("")
      setName("")
      setDescription("")
      setVersion("1")
      setIsActive(true)
      setRequiresExpirationDate(false)
      setValidityDays("")
      setInputs([emptyInputRow()])
      setOutputs([emptyOutputRow(true)])
      onSuccess?.()
    },
    onError: (err: any, submitted) => {
      const body = err?.body
      if (
        typeof body?.error === "string" &&
        body.error.includes("Ten sam materiał został użyty więcej niż raz")
      ) {
        toast({
          title: "Materiał został użyty więcej niż raz",
          description: body.error,
          status: "error",
          duration: 7000,
        })
        return
      }
      if (
        formMode !== "version" &&
        body?.code === "duplicate_technology_code"
      ) {
        const suggested =
          typeof body.suggested_code === "string" && body.suggested_code.trim()
            ? body.suggested_code.trim()
            : null
        setCodeConflict({
          requested: normalizeTechnologyCode(submitted.code),
          suggested,
        })
        toast({
          title: "Kod technologii jest już zajęty",
          description:
            body?.error ?? "Technologia z podanym kodem już istnieje.",
          status: "error",
        })
        return
      }
      const licenseError = getLicenseErrorDetails(err)
      toast({
        title: licenseError?.title ?? "Błąd tworzenia",
        description:
          licenseError?.description ??
          body?.error ??
          err?.message ??
          "Spróbuj ponownie",
        status: "error",
        duration: 7000,
      })
    },
  })

  const openPicker = (
    type: "input" | "output",
    mode: "append" | "replace",
    index?: number,
  ) => {
    setPickerTarget({ type, mode, index })
    setPickerOpen(true)
  }

  const openSubPicker = (
    inputIndex: number,
    mode: "append" | "replace",
    subIndex?: number,
  ) => {
    setPickerTarget({ type: "substitute", mode, inputIndex, subIndex })
    setPickerOpen(true)
  }

  const handleChooseProduct = (p: ProductListItemDTO) => {
    if (!pickerTarget) return
    const target =
      pickerTarget.type === "substitute"
        ? {
            role: "substitute" as const,
            inputIndex: pickerTarget.inputIndex,
            substituteIndex: pickerTarget.subIndex,
          }
        : pickerTarget.mode === "replace" && pickerTarget.index != null
          ? {
              role: pickerTarget.type as "primary",
              inputIndex: pickerTarget.index,
            }
          : undefined
    if (pickerTarget.type === "input" || pickerTarget.type === "substitute") {
      const existingUsages = findTechnologyInputProductUsagesOutsideTarget(
        inputProductRows,
        p.id,
        target,
      )
      if (existingUsages.length > 0) {
        const productLabel = `${p.sku} — ${p.name}`
        toast({
          title: "Ten materiał jest już użyty",
          description: `Produkt „${productLabel}” jest już użyty ${existingUsages.map(formatTechnologyInputProductUsage).join(" oraz ")}. Każdy materiał może wystąpić tylko raz w wejściach technologii — jako materiał podstawowy albo jako zamiennik. Wybierz inny produkt.`,
          status: "error",
          duration: 7000,
        })
        return
      }
    }
    if (pickerTarget.type === "substitute") {
      const { inputIndex, subIndex, mode } = pickerTarget
      setInputs((prev) => {
        const next = [...prev]
        const currentSubs = next[inputIndex].substitutes
          ? [...next[inputIndex].substitutes]
          : []
        if (mode === "append") {
          currentSubs.push({ ...emptySubstituteRow(), product: p })
        } else if (subIndex != null) {
          currentSubs[subIndex] = { ...currentSubs[subIndex], product: p }
        }
        next[inputIndex] = { ...next[inputIndex], substitutes: currentSubs }
        return next
      })
    } else if (pickerTarget.mode === "append") {
      if (pickerTarget.type === "input") {
        setInputs((prev) => [...prev, { ...emptyInputRow(), product: p }])
      } else {
        setOutputs((prev) => [...prev, { ...emptyOutputRow(), product: p }])
      }
    } else if (pickerTarget.type === "input" && pickerTarget.index != null) {
      setInputs((prev) => {
        const next = [...prev]
        next[pickerTarget.index!] = {
          ...next[pickerTarget.index!],
          product: p,
        }
        return next
      })
    } else if (pickerTarget.type === "output" && pickerTarget.index != null) {
      setOutputs((prev) => {
        const next = [...prev]
        next[pickerTarget.index!] = {
          ...next[pickerTarget.index!],
          product: p,
        }
        return next
      })
    }
    setPickerOpen(false)
    setPickerTarget(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    const payload: TechnologyCreateDTO = {
      code: normalizeTechnologyCode(code),
      name: name.trim(),
      description: description.trim() || undefined,
      version: Number.parseInt(version, 10) || 1,
      is_active: isActive,
      requires_expiration_date: requiresExpirationDate,
      validity_days:
        validityDays.trim() !== ""
          ? Number.parseInt(validityDays, 10)
          : undefined,
      inputs: inputs.map((row) => ({
        product_id: row.product?.id ?? "",
        qty: Number.parseFloat(row.qty),
        loss_percent:
          row.loss_percent.trim() === ""
            ? undefined
            : Number.parseFloat(row.loss_percent),
        cost_mode: row.product?.is_service
          ? "fixed_service_rate"
          : "purchase_history",
        service_cost_netto: row.product?.is_service
          ? Number.parseFloat(row.service_cost_netto)
          : undefined,
        service_cost_source: row.product?.is_service
          ? "technology_fixed_rate"
          : undefined,
        cost_note: row.cost_note.trim() || undefined,
        substitutes: row.substitutes
          ?.filter((sub) => sub.product?.id && Number.parseFloat(sub.qty) > 0)
          .map((sub, sIdx) => ({
            substitute_product_id: sub.product!.id,
            qty: Number.parseFloat(sub.qty),
            loss_percent:
              sub.loss_percent.trim() === ""
                ? undefined
                : Number.parseFloat(sub.loss_percent),
            cost_mode: sub.product?.is_service
              ? "fixed_service_rate"
              : "purchase_history",
            service_cost_netto: sub.product?.is_service
              ? Number.parseFloat(sub.service_cost_netto)
              : undefined,
            service_cost_source: sub.product?.is_service
              ? "technology_fixed_rate"
              : undefined,
            priority: Number.parseInt(sub.priority, 10) || sIdx + 1,
            note: sub.note.trim() || undefined,
          })),
      })),
      outputs: outputs.map((row) => ({
        product_id: row.product?.id ?? "",
        qty: Number.parseFloat(row.qty),
        is_main: row.is_main,
      })),
    }

    mutate(payload)
  }

  return (
    <VStack as="form" onSubmit={handleSubmit} align="stretch" spacing={4}>
      {codeConflict &&
        codeConflict.requested === normalizeTechnologyCode(code) && (
          <Alert
            status="warning"
            borderRadius="xl"
            alignItems={{ base: "flex-start", md: "center" }}
            flexDirection={{ base: "column", md: "row" }}
            gap={3}
          >
            <AlertIcon mt={{ base: 1, md: 0 }} />
            <Box flex="1">
              <Text fontWeight="semibold">
                Kod „{codeConflict.requested}” jest już zajęty.
              </Text>
              <Text fontSize="sm">
                {codeConflict.suggested
                  ? `Następny dostępny wariant: ${codeConflict.suggested}`
                  : "Wprowadź inny, nieużywany kod technologii."}
              </Text>
            </Box>
            {codeConflict.suggested && (
              <Button
                type="button"
                colorScheme="orange"
                size="sm"
                onClick={() => {
                  setCode(codeConflict.suggested ?? code)
                  setVersion("1")
                  setCodeConflict(null)
                }}
              >
                Użyj {codeConflict.suggested}
              </Button>
            )}
          </Alert>
        )}
      <Box
        bg={panelBg}
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="xl"
        p={4}
      >
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          }}
          gap={3}
          alignItems="start"
        >
          <GridItem>
            <FormControl isInvalid={isCodeErr} isRequired>
              <FormLabel mb={1} fontSize="sm">
                Kod technologii
              </FormLabel>
              <Input
                size="sm"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.toLocaleUpperCase("pl-PL"))
                }
                placeholder="TECH-001"
                borderRadius="lg"
              />
              <FormErrorMessage>Pole Kod jest obowiązkowe</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl isInvalid={isNameErr} isRequired>
              <FormLabel mb={1} fontSize="sm">
                Nazwa
              </FormLabel>
              <Input
                size="sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nazwa technologii"
                borderRadius="lg"
              />
              <FormErrorMessage>Pole Nazwa jest obowiązkowe</FormErrorMessage>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl maxW={{ base: "auto", md: "180px" }}>
              <FormLabel mb={1} fontSize="sm">
                Wersja
              </FormLabel>
              <Input
                size="sm"
                type="number"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                borderRadius="lg"
                isDisabled={isVersionLocked}
              />
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl h="full">
              <FormLabel mb={1} fontSize="sm">
                Aktywna
              </FormLabel>
              <Checkbox
                pt={2}
                isChecked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              >
                Tak
              </Checkbox>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 1, md: 2, xl: 4 }}>
            <FormControl>
              <FormLabel mb={1} fontSize="sm">
                Opis
              </FormLabel>
              <Textarea
                size="sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Opcjonalnie"
                borderRadius="lg"
                minH="88px"
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 1, md: 2, xl: 4 }}>
            <Box
              p={3}
              borderWidth="1px"
              borderColor={panelBorder}
              borderRadius="lg"
              bg={useColorModeValue("gray.50", "whiteAlpha.50")}
            >
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={4}
                alignItems="center"
              >
                <FormControl>
                  <FormLabel mb={0} fontSize="sm" fontWeight="bold">
                    Termin przydatności przy produkcji
                  </FormLabel>
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Określa, czy technologia wymusza obliczanie daty ważności
                    przy tworzeniu meldunku.
                  </Text>
                  <Checkbox
                    isChecked={requiresExpirationDate}
                    onChange={(e) =>
                      setRequiresExpirationDate(e.target.checked)
                    }
                    colorScheme="orange"
                  >
                    Uwzględnij termin przydatności
                  </Checkbox>
                  {mainProductRequiresExpiration && !requiresExpirationDate && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      Uwaga: Główny produkt wyjściowy ({mainOutputProduct?.sku})
                      wymaga daty ważności. Ta opcja musi być zaznaczona!
                    </Text>
                  )}
                </FormControl>

                {requiresExpirationDate && (
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Dni przydatności do spożycia (od daty produkcji)
                    </FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      value={validityDays}
                      onChange={(e) => setValidityDays(e.target.value)}
                      placeholder="Np. 180"
                      borderRadius="lg"
                    />
                  </FormControl>
                )}
              </Grid>
            </Box>
          </GridItem>
        </Grid>
      </Box>

      <Divider />

      <Box>
        <HStack justify="space-between" mb={2} align="center">
          <Text fontWeight="bold" fontSize="sm">
            Wejścia (inputs)
          </Text>
          <Button
            size="sm"
            colorScheme="green"
            leftIcon={<AddIcon boxSize={3} />}
            onClick={() => openPicker("input", "append")}
          >
            Dodaj pozycję
          </Button>
        </HStack>
        {inputProductConflicts.length > 0 && (
          <Alert
            status="error"
            borderRadius="md"
            mb={3}
            alignItems="flex-start"
          >
            <AlertIcon mt={1} />
            <Box>
              <Text fontWeight="semibold">
                Materiał został użyty więcej niż raz
              </Text>
              <Text fontSize="sm">
                Każdy materiał może wystąpić tylko raz w wejściach technologii —
                jako materiał podstawowy albo jako zamiennik. Usuń powtórzenie
                lub wybierz inny produkt.
              </Text>
              {inputProductConflicts.map((conflict) => {
                const label =
                  conflict.usages.find((usage) => usage.productLabel)
                    ?.productLabel ?? "Nieznany materiał"
                return (
                  <Text key={conflict.productId} fontSize="sm" mt={1}>
                    Produkt „{label}”:{" "}
                    {conflict.usages
                      .map(formatTechnologyInputProductUsage)
                      .join(" oraz ")}
                    .
                  </Text>
                )
              })}
            </Box>
          </Alert>
        )}
        <TableContainer
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="xl"
          overflowX="auto"
        >
          <Table size="sm" sx={{ tableLayout: "fixed" }}>
            <Thead>
              <Tr>
                <Th w="30%">Produkt</Th>
                <Th w="90px">Ilość</Th>
                <Th w="90px">Strata %</Th>
                <Th w="150px">Koszt</Th>
                <Th w="72px">Akcja</Th>
              </Tr>
            </Thead>
            <Tbody>
              {inputs.map((row, idx) => (
                <Fragment key={`input-${idx}`}>
                  <Tr verticalAlign="top">
                    <Td py={2}>
                      <RowProductSelector
                        size="sm"
                        value={
                          row.product
                            ? `${row.product.sku} — ${row.product.name}`
                            : ""
                        }
                        onOpenPicker={() => openPicker("input", "replace", idx)}
                        showLabel={false}
                        placeholder="Nie wybrano produktu"
                      />
                    </Td>
                    <Td py={2}>
                      <Input
                        size="sm"
                        type="number"
                        step="any"
                        value={row.qty}
                        onChange={(e) => {
                          const value = e.target.value
                          setInputs((prev) => {
                            const next = [...prev]
                            next[idx] = { ...next[idx], qty: value }
                            return next
                          })
                        }}
                      />
                    </Td>
                    <Td py={2}>
                      <Input
                        size="sm"
                        type="number"
                        step="any"
                        value={row.loss_percent}
                        onChange={(e) => {
                          const value = e.target.value
                          setInputs((prev) => {
                            const next = [...prev]
                            next[idx] = { ...next[idx], loss_percent: value }
                            return next
                          })
                        }}
                      />
                    </Td>
                    <Td py={2}>
                      <Input
                        size="sm"
                        type="number"
                        step="0.0001"
                        value={row.service_cost_netto}
                        isDisabled={!row.product?.is_service}
                        placeholder={
                          row.product?.is_service
                            ? "Koszt netto usługi"
                            : "Historia PZ"
                        }
                        onChange={(e) => {
                          const value = e.target.value
                          setInputs((prev) => {
                            const next = [...prev]
                            next[idx] = {
                              ...next[idx],
                              service_cost_netto: value,
                            }
                            return next
                          })
                        }}
                      />
                    </Td>
                    <Td py={2}>
                      <HStack spacing={1}>
                        <Button
                          size="xs"
                          colorScheme="purple"
                          variant="ghost"
                          onClick={() => openSubPicker(idx, "append")}
                        >
                          + Zamiennik
                        </Button>
                        <IconButton
                          size="xs"
                          variant="solid"
                          colorScheme="red"
                          boxShadow="sm"
                          aria-label="Usuń pozycję"
                          icon={<DeleteIcon boxSize={3} />}
                          onClick={() =>
                            setInputs((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          isDisabled={inputs.length === 1}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                  {row.substitutes && row.substitutes.length > 0 && (
                    <Tr bg="purple.50">
                      <Td colSpan={5} py={2} pl={6}>
                        <VStack align="stretch" spacing={2}>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="purple.700"
                          >
                            Zamienniki materiału (
                            {row.product?.name ?? `Pozycja #${idx + 1}`}):
                          </Text>
                          {row.substitutes.map((sub, sIdx) => (
                            <HStack
                              key={`sub-${idx}-${sIdx}`}
                              spacing={2}
                              align="center"
                            >
                              <RowProductSelector
                                size="sm"
                                value={
                                  sub.product
                                    ? `${sub.product.sku} — ${sub.product.name}`
                                    : ""
                                }
                                onOpenPicker={() =>
                                  openSubPicker(idx, "replace", sIdx)
                                }
                                showLabel={false}
                                placeholder="Wybierz zamiennik"
                              />
                              <Input
                                size="xs"
                                w="90px"
                                type="number"
                                step="any"
                                placeholder="Ilość"
                                value={sub.qty}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setInputs((prev) => {
                                    const next = [...prev]
                                    const subs = [
                                      ...(next[idx].substitutes ?? []),
                                    ]
                                    subs[sIdx] = { ...subs[sIdx], qty: val }
                                    next[idx] = {
                                      ...next[idx],
                                      substitutes: subs,
                                    }
                                    return next
                                  })
                                }}
                              />
                              <Input
                                size="xs"
                                w="130px"
                                type="number"
                                step="0.0001"
                                placeholder={
                                  sub.product?.is_service
                                    ? "Koszt netto usługi"
                                    : "Historia PZ"
                                }
                                isDisabled={!sub.product?.is_service}
                                value={sub.service_cost_netto}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setInputs((prev) => {
                                    const next = [...prev]
                                    const subs = [
                                      ...(next[idx].substitutes ?? []),
                                    ]
                                    subs[sIdx] = {
                                      ...subs[sIdx],
                                      service_cost_netto: val,
                                    }
                                    next[idx] = {
                                      ...next[idx],
                                      substitutes: subs,
                                    }
                                    return next
                                  })
                                }}
                              />
                              <Input
                                size="xs"
                                w="70px"
                                type="number"
                                min={1}
                                step={1}
                                placeholder="Priorytet"
                                value={sub.priority}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setInputs((prev) => {
                                    const next = [...prev]
                                    const subs = [
                                      ...(next[idx].substitutes ?? []),
                                    ]
                                    subs[sIdx] = {
                                      ...subs[sIdx],
                                      priority: val,
                                    }
                                    next[idx] = {
                                      ...next[idx],
                                      substitutes: subs,
                                    }
                                    return next
                                  })
                                }}
                              />
                              <Input
                                size="xs"
                                w="90px"
                                type="number"
                                step="any"
                                placeholder="Strata %"
                                value={sub.loss_percent}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setInputs((prev) =>
                                    updateSubstituteLossPercent(
                                      prev,
                                      idx,
                                      sIdx,
                                      val,
                                    ),
                                  )
                                }}
                              />
                              <Input
                                size="xs"
                                flex="1"
                                placeholder="Uwagi (np. marka, wyższa zużywalność)"
                                value={sub.note}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setInputs((prev) => {
                                    const next = [...prev]
                                    const subs = [
                                      ...(next[idx].substitutes ?? []),
                                    ]
                                    subs[sIdx] = { ...subs[sIdx], note: val }
                                    next[idx] = {
                                      ...next[idx],
                                      substitutes: subs,
                                    }
                                    return next
                                  })
                                }}
                              />
                              <IconButton
                                size="xs"
                                aria-label="Usuń zamiennik"
                                icon={<DeleteIcon boxSize={3} />}
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => {
                                  setInputs((prev) => {
                                    const next = [...prev]
                                    const subs = (
                                      next[idx].substitutes ?? []
                                    ).filter((_, i) => i !== sIdx)
                                    next[idx] = {
                                      ...next[idx],
                                      substitutes: subs,
                                    }
                                    return next
                                  })
                                }}
                              />
                            </HStack>
                          ))}
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        {!inputsValid && (
          <Text color="red.500" fontSize="xs" mt={2}>
            Wszystkie wejścia muszą mieć produkt i ilość &gt; 0. Dla usług
            wymagany jest koszt netto &gt; 0.
          </Text>
        )}
      </Box>

      <Box>
        <HStack justify="space-between" mb={2} align="center">
          <Text fontWeight="bold" fontSize="sm">
            Wyjścia (outputs)
          </Text>
          <Button
            size="sm"
            colorScheme="green"
            leftIcon={<AddIcon boxSize={3} />}
            onClick={() => openPicker("output", "append")}
          >
            Dodaj pozycję
          </Button>
        </HStack>
        <TableContainer
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="xl"
          overflowX="auto"
        >
          <Table size="sm" sx={{ tableLayout: "fixed" }}>
            <Thead>
              <Tr>
                <Th w="48%">Produkt</Th>
                <Th w="100px">Ilość</Th>
                <Th w="120px">Referencyjny</Th>
                <Th w="72px">Akcja</Th>
              </Tr>
            </Thead>
            <Tbody>
              {outputs.map((row, idx) => (
                <Tr key={`output-${idx}`} verticalAlign="top">
                  <Td py={2}>
                    <RowProductSelector
                      size="sm"
                      value={
                        row.product
                          ? `${row.product.sku} — ${row.product.name}`
                          : ""
                      }
                      onOpenPicker={() => openPicker("output", "replace", idx)}
                      showLabel={false}
                      placeholder="Nie wybrano produktu"
                    />
                  </Td>
                  <Td py={2}>
                    <Input
                      size="sm"
                      type="number"
                      step="any"
                      value={row.qty}
                      onChange={(e) => {
                        const value = e.target.value
                        setOutputs((prev) => {
                          const next = [...prev]
                          next[idx] = { ...next[idx], qty: value }
                          return next
                        })
                      }}
                    />
                  </Td>
                  <Td py={2} textAlign="center">
                    <Radio
                      isChecked={row.is_main}
                      onChange={() => {
                        setOutputs((prev) => {
                          return prev.map((item, itemIdx) => ({
                            ...item,
                            is_main: itemIdx === idx,
                          }))
                        })
                      }}
                    />
                  </Td>
                  <Td py={2}>
                    <IconButton
                      size="xs"
                      variant="solid"
                      colorScheme="red"
                      boxShadow="sm"
                      aria-label="Usuń pozycję"
                      icon={<DeleteIcon boxSize={3} />}
                      onClick={() => {
                        setOutputs((prev) => {
                          const next = prev.filter((_, i) => i !== idx)
                          if (!next.some((item) => item.is_main)) {
                            next[0] = { ...next[0], is_main: true }
                          }
                          return next
                        })
                      }}
                      isDisabled={outputs.length === 1}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        {!outputsValid && (
          <Text color="red.500" fontSize="sm" mt={2}>
            Wszystkie wyjścia muszą mieć produkt i ilość &gt; 0. Jedno wyjście
            musi być oznaczone jako referencyjne.
          </Text>
        )}
        <Text color="gray.500" fontSize="sm" mt={2}>
          Zaznacz dokładnie jeden produkt referencyjny. Jego ilość steruje
          skalowaniem zlecenia; pozostałe wyjścia też zostaną wyprodukowane
          proporcjonalnie.
        </Text>
      </Box>

      <HStack justify="flex-end">
        <Button variant="ghost" onClick={onCancel} type="button">
          Anuluj
        </Button>
        <Button
          colorScheme="blue"
          type="submit"
          isLoading={isPending}
          isDisabled={!canSubmit}
        >
          {formMode === "version" ? "Utwórz wersję" : "Utwórz"}
        </Button>
      </HStack>

      <UnifiedProductPickerModal
        isOpen={pickerOpen}
        onClose={() => {
          setPickerOpen(false)
          setPickerTarget(null)
        }}
        title="Wybierz produkt"
        catalogOnly
        highlightedProductIds={highlightedProductIds}
        onChoose={(_, __, ___, ____, product) => {
          if (product) handleChooseProduct(product)
        }}
      />
    </VStack>
  )
}
